import { FC, ChangeEventHandler } from 'react';
import classNames from 'classnames';

import InputError from '../inputError';

interface Option {
  name: string;
  value: any;
  disabled?: boolean;
  checked?: boolean;
}

type OnChange = ChangeEventHandler<HTMLInputElement>;

interface Props {
  type?: string;
  className?: string;
  loading?: boolean;
  hideLabel?: boolean;
  name: string;
  options?: Option[];
  id: string | number;
  value?: any[];
  setValue?: (value: any[]) => void;
  onChange?: OnChange;
  error?: boolean;
  errorMsg?: string;
}

const CheckBoxList: FC<Props> = ({
  type: inputType = 'checkbox',
  className,
  loading = false,
  name,
  id,
  value: checkedList = [],
  setValue,
  options = [],
  hideLabel = false,
  onChange: onChangeProp = () => null,
  error = false,
  errorMsg,
}) => {
  const classes = classNames(
    'field',
    'field--checkbox-list',
    {
      'field--hidden-label': !!hideLabel,
      'field--loading': !!loading,
      'field--has-error': !!error,
    },
    className
  );

  const renderItem = ({
    name: label,
    value,
    disabled = false,
    checked = false,
  }) => {
    const itemClasses = classNames('field--checkbox-list__item', {
      'field--checkbox-list__item--disabled': !!disabled,
      'field--checkbox-list__item--checked': !!checked,
    });

    const onChange: OnChange = e =>
      setValue
        ? setValue(
            checkedList.includes(value)
              ? checkedList.filter(v => v !== value)
              : [...checkedList, value]
          )
        : onChangeProp(e);

    return (
      <li className={itemClasses} key={value} data-testid="checkBoxListItem">
        <label htmlFor={`${id}-${value}`}>
          <input
            id={`${id}-${value}`}
            type={inputType}
            key={value}
            data-testid={`${name}${value}`}
            name={name}
            value={value}
            checked={setValue ? checkedList.includes(value) : checked}
            disabled={disabled}
            data-type={typeof value}
            onChange={disabled ? null : onChange}
          />
          {label}
        </label>
      </li>
    );
  };

  return (
    <div className={classes} data-testid="checkBoxList" data-cy="checkBoxList">
      <ul>{options.map(renderItem)}</ul>
      {error && <InputError errorMsg={errorMsg} />}
    </div>
  );
};

export default CheckBoxList;
